import { TipoChavePix, PerfilEmpresaInput } from '@/contracts/schemas';
import { renderQrSvg } from './qr';

export interface PixParams {
  chavePix: string;
  tipoChave?: TipoChavePix;
  nomeRecebedor: string;
  cidadeRecebedor: string;
  valor?: number;
  txId?: string;
  descricao?: string;
}

export interface PixGenerationResult {
  brCode: string;
  payloadSemCRC: string;
  crc: string;
  txId: string;
  valor: number;
}

/**
 * Formata um campo no padrão TLV (Tag-Length-Value) do EMV-QRCPS.
 * @param id Identificador da Tag (2 dígitos)
 * @param value Conteúdo do campo
 */
export function formatTLV(id: string, value: string): string {
  const lengthStr = value.length.toString().padStart(2, '0');
  return `${id}${lengthStr}${value}`;
}

/**
 * Normaliza o texto para o padrão Pix (ASCII, sem acentos, maiúsculas, tamanho máximo).
 */
export function sanitizePixText(text: string, maxLength: number): string {
  if (!text) return '';
  const normalized = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .toUpperCase();
  return normalized.substring(0, maxLength);
}

/**
 * Calcula o CRC16-CCITT no padrão oficial do Banco Central do Brasil (Polinômio 0x1021, Init 0xFFFF).
 */
export function calculateCRC16(payload: string): string {
  let crc = 0xFFFF;
  const polynomial = 0x1021;

  for (let i = 0; i < payload.length; i++) {
    crc ^= (payload.charCodeAt(i) << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ polynomial) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }

  return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
}

/**
 * Monta o Payload oficial BRCode Pix estático/dinâmico e anexa o CRC16.
 */
export function generatePixPayload(params: PixParams): PixGenerationResult {
  const chavePixLimpa = params.chavePix.trim();
  const nomeSanitizado = sanitizePixText(params.nomeRecebedor || 'RECEBEDOR', 25) || 'RECEBEDOR';
  const cidadeSanitizada = sanitizePixText(params.cidadeRecebedor || 'BRASILIA', 15) || 'BRASILIA';
  const txIdSanitizado = sanitizePixText(params.txId || '***', 25) || '***';
  const valorFormatado = params.valor && params.valor > 0 ? params.valor.toFixed(2) : undefined;

  // Tag 00: Payload Format Indicator
  let payload = formatTLV('00', '01');

  // Tag 01: Point of Initiation Method (12 = Estático/reutilizável)
  payload += formatTLV('01', '12');

  // Tag 26: Merchant Account Information
  let merchantAccount = formatTLV('00', 'BR.GOV.BCB.PIX');
  merchantAccount += formatTLV('01', chavePixLimpa);
  if (params.descricao) {
    const descSanitizada = sanitizePixText(params.descricao, 40);
    if (descSanitizada) {
      merchantAccount += formatTLV('02', descSanitizada);
    }
  }
  payload += formatTLV('26', merchantAccount);

  // Tag 52: Merchant Category Code (0000 = padrão)
  payload += formatTLV('52', '0000');

  // Tag 53: Transaction Currency (986 = BRL)
  payload += formatTLV('53', '986');

  // Tag 54: Transaction Amount
  if (valorFormatado) {
    payload += formatTLV('54', valorFormatado);
  }

  // Tag 58: Country Code
  payload += formatTLV('58', 'BR');

  // Tag 59: Merchant Name
  payload += formatTLV('59', nomeSanitizado);

  // Tag 60: Merchant City
  payload += formatTLV('60', cidadeSanitizada);

  // Tag 62: Additional Data Field (TxID)
  const additionalData = formatTLV('05', txIdSanitizado);
  payload += formatTLV('62', additionalData);

  // Tag 63: CRC16 prefix
  const payloadParaCRC = `${payload}6304`;
  const crc = calculateCRC16(payloadParaCRC);
  const brCode = `${payloadParaCRC}${crc}`;

  return {
    brCode,
    payloadSemCRC: payloadParaCRC,
    crc,
    txId: txIdSanitizado,
    valor: params.valor || 0
  };
}

/**
 * Gera o link direto de envio para o WhatsApp (wa.me) com texto codificado.
 */
export function generateWhatsAppShareUrl(phone: string, text: string): string {
  const digitsOnly = phone.replace(/\D/g, '');
  const fullNumber = digitsOnly.startsWith('55') ? digitsOnly : `55${digitsOnly}`;
  const encodedText = encodeURIComponent(text);
  return `https://wa.me/${fullNumber}?text=${encodedText}`;
}

/**
 * Renderiza o QR Code em formato SVG string.
 */
export async function generateQrCodeSvg(text: string): Promise<string> {
  return renderQrSvg(text, {
    margin: 1,
    dark: '#0f172a',
    light: '#ffffff'
  });
}

/**
 * Renderiza o QR Code em formato DataURL (Base64 PNG) para uso em imagens.
 */
export async function generateQrCodeDataUrl(text: string): Promise<string> {
  const svg = renderQrSvg(text, {
    margin: 1,
    width: 256,
    dark: '#0f172a',
    light: '#ffffff'
  });
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Valida se um CPF é matematicamente válido com dígitos verificadores.
 */
export function validateCpf(cpfRaw: string): boolean {
  const cpf = cpfRaw.replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (10 - i);
  }
  let rev = 11 - (sum % 11);
  let d1 = (rev >= 10) ? 0 : rev;
  if (d1 !== parseInt(cpf.charAt(9), 10)) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (11 - i);
  }
  rev = 11 - (sum % 11);
  let d2 = (rev >= 10) ? 0 : rev;
  return d2 === parseInt(cpf.charAt(10), 10);
}

/**
 * Valida se um CNPJ é matematicamente válido com dígitos verificadores.
 */
export function validateCnpj(cnpjRaw: string): boolean {
  const cnpj = cnpjRaw.replace(/\D/g, '');
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  let length = 12;
  let numbers = cnpj.substring(0, length);
  let digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0), 10)) return false;

  length = 13;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === parseInt(digits.charAt(1), 10);
}

/**
 * Valida uma Chave Pix de acordo com as normas estritas do Banco Central do Brasil.
 */
export function validatePixKey(key: string, tipo: TipoChavePix): { valid: boolean; error?: string } {
  const trimmed = key.trim();
  if (!trimmed) {
    return { valid: false, error: 'A chave Pix é obrigatória' };
  }

  switch (tipo) {
    case 'cpf': {
      const numbers = trimmed.replace(/\D/g, '');
      if (numbers.length !== 11) {
        return { valid: false, error: 'Chave CPF deve conter 11 dígitos' };
      }
      if (!validateCpf(numbers)) {
        return { valid: false, error: 'Chave CPF inválida' };
      }
      return { valid: true };
    }

    case 'cnpj': {
      const numbers = trimmed.replace(/\D/g, '');
      if (numbers.length !== 14) {
        return { valid: false, error: 'Chave CNPJ deve conter 14 dígitos' };
      }
      if (!validateCnpj(numbers)) {
        return { valid: false, error: 'Chave CNPJ inválida' };
      }
      return { valid: true };
    }

    case 'email': {
      if (trimmed.length > 77) {
        return { valid: false, error: 'Chave de e-mail excede o limite de 77 caracteres do BACEN' };
      }
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(trimmed)) {
        return { valid: false, error: 'Formato de e-mail inválido para chave Pix' };
      }
      return { valid: true };
    }

    case 'telefone': {
      const numbers = trimmed.replace(/\D/g, '');
      // Formatos válidos: 10 ou 11 dígitos (DDD + número) ou 12 ou 13 dígitos (+55...)
      if (numbers.length === 10 || numbers.length === 11) {
        return { valid: true };
      }
      if (numbers.startsWith('55') && (numbers.length === 12 || numbers.length === 13)) {
        return { valid: true };
      }
      return {
        valid: false,
        error: 'Chave de telefone deve conter DDD + 8 ou 9 dígitos (ex: 11987654321)'
      };
    }

    case 'aleatoria': {
      // Padrão UUIDv4 (EVP) do BACEN
      const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      if (!uuidRegex.test(trimmed)) {
        return {
          valid: false,
          error: 'Chave aleatória deve seguir o padrão UUID (ex: 123e4567-e89b-12d3-a456-426614174000)'
        };
      }
      return { valid: true };
    }

    default:
      return { valid: false, error: 'Tipo de chave Pix desconhecido' };
  }
}

/**
 * Valida se um documento é um CPF (11 dígitos) ou CNPJ (14 dígitos) matematicamente válido.
 */
export function validateDocumento(docRaw: string): { valid: boolean; tipo?: 'cpf' | 'cnpj'; error?: string } {
  const digits = docRaw.replace(/\D/g, '');
  if (digits.length === 11) {
    const ok = validateCpf(digits);
    return ok ? { valid: true, tipo: 'cpf' } : { valid: false, error: 'CPF com dígitos verificadores inválidos' };
  }
  if (digits.length === 14) {
    const ok = validateCnpj(digits);
    return ok ? { valid: true, tipo: 'cnpj' } : { valid: false, error: 'CNPJ com dígitos verificadores inválidos' };
  }
  return { valid: false, error: 'Documento deve conter 11 (CPF) ou 14 (CNPJ) dígitos' };
}

/**
 * Validação cruzada completa dos dados do perfil da empresa/MEI.
 */
export function validatePerfilEmpresa(input: PerfilEmpresaInput): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!input.nome || input.nome.trim().length < 3) {
    errors.nome = 'Razão Social / Nome deve conter no mínimo 3 caracteres';
  }

  const docRes = validateDocumento(input.documento);
  if (!docRes.valid) {
    errors.documento = docRes.error || 'Documento inválido';
  }

  const pixRes = validatePixKey(input.chavePix, input.tipoChavePix);
  if (!pixRes.valid) {
    errors.chavePix = pixRes.error || 'Chave Pix inválida para o tipo selecionado';
  }

  if (!input.cidade || input.cidade.trim().length < 2) {
    errors.cidade = 'Cidade é obrigatória';
  } else if (input.cidade.trim().length > 15) {
    errors.cidade = 'Cidade deve conter no máximo 15 caracteres no padrão Pix';
  }

  if (!input.uf || input.uf.trim().length !== 2) {
    errors.uf = 'UF deve conter exatamente 2 letras';
  }

  if (!input.telefone || input.telefone.replace(/\D/g, '').length < 10) {
    errors.telefone = 'Telefone com DDD é obrigatório (mínimo 10 dígitos)';
  }

  if (!input.ramoAtividade || input.ramoAtividade.trim().length < 2) {
    errors.ramoAtividade = 'Ramo de atividade é obrigatório';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}


